import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@components/Box/Box";
import styles from "./styles.module.scss";
import classnames from "classnames";
import { Button } from "@components/Button/Button";
import { PrimitiveProps } from "@helpers/interfaces/primitive";
import { Container } from "@components/Container/Container";
import { useBetween } from "use-between";
import { Heading } from "@components/Heading/Heading";
import { useMediaQuery } from "@react-hook/media-query";
import { Link } from "@components/Link/Link";
import anime from "animejs";
import { animeChildrenProps } from "@helpers/animations";
import Anime from "react-anime";
import { useDetectClickOutside } from "react-detect-click-outside";

interface UseNavToggleProps {
  isActive: boolean;
  setActive: () => void;
  setNotActive: () => void;
}
const useNavToggle = (): UseNavToggleProps => {
  const [isActive, setState] = useState<boolean>(false);
  const setActive = useCallback(() => setState(true), []);
  const setNotActive = useCallback(() => setState(false), []);
  return {
    isActive,
    setActive,
    setNotActive
  };
};

export const useSharedNavToggleState = (): UseNavToggleProps =>
  useBetween(useNavToggle);

const NavigationIcon: React.FC<{
  active: boolean;
  activeClassName?: string;
  className?: string;
}> = ({ active, className, activeClassName }) => {
  
  return (
    <span
      className={classnames(
        active &&
          (activeClassName ? activeClassName : styles.naivgationIconActive),
        className && className
      )}
    >
      <span></span>
      <span></span>
      <span></span>
    </span>
  );
};

interface SubItemProps {
  Id: number;
  Name: string;
  Url: string;
  Target: string;
  UmbracoNaviHide?: boolean;
}
interface MappedMenuPageItemsProps {
  Id: number;
  Name: string;
  MappedMenuPageItems: SubItemProps[];
  UmbracoNaviHide?: boolean;
}
export interface MappedMenuItemProps {
  Id: number;
  Name: string;
  MappedMenuItems: MappedMenuPageItemsProps[];
  UmbracoNaviHide?: boolean;
}
export interface NavigationProps extends PrimitiveProps {
  navigation: MappedMenuItemProps[];
  useDarkHeader?: boolean;
}

const Navigation: React.FC<NavigationProps> = ({
  className,
  navigation,
  useDarkHeader = false,
  ...props
}) => {
  const [selectedNav, setSelectedNav] = useState<number>(0);
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  const { setActive, setNotActive } = useSharedNavToggleState();
  const isMobile = useMediaQuery("only screen and (min-width: 1024px)");
  const overlayRef = useRef<HTMLDivElement[]>([]);

  const animateNavigation = (target: HTMLDivElement, open: boolean): void => {
    anime({
      targets: target,
      translateY: open ? "0%" : "-100%",
      duration: 200,
      easing: open ? "easeOutQuart" : "easeInQuart",
      begin: () => {
        if (open) {
          target.removeAttribute("hidden");
        }
      },
      complete: () => {
        if (!open) {
          target.setAttribute("hidden", "");
        }
      }
    });
  };

  const toggle: React.MouseEventHandler<HTMLButtonElement> = e => {
    const key = e.currentTarget.dataset.key
      ? parseInt(e.currentTarget.dataset.key)
      : 0;
    const isActive = key > 0 && selectedNav !== key;

    if (!mobileNavOpen) {
      if (selectedNav > 0) {
        animateNavigation(overlayRef.current[selectedNav - 1], false);
      }
      if (isActive) {
        setSelectedNav(key);
        animateNavigation(overlayRef.current[key - 1], true);
      } else {
        setSelectedNav(0);
      }

      if (isActive) {
        setActive();
      } else {
        setNotActive();
      }
    } else {
      if (selectedNav !== key) {
        animateNavigation(overlayRef.current[selectedNav - 1], false);
      }
      animateNavigation(overlayRef.current[key - 1], true);
      setSelectedNav(key);
    }
  };

  const toggleMobileNavigation: React.MouseEventHandler<HTMLButtonElement> = () => {
    if (!mobileNavOpen) {
      setMobileNavOpen(true);
      animateNavigation(overlayRef.current[0], true);
      setSelectedNav(1);
      setActive();
    } else {
      setMobileNavOpen(false);
      animateNavigation(overlayRef.current[selectedNav - 1], false);
      setSelectedNav(0);
      setNotActive();
    }
  };

  const ref = useDetectClickOutside({
    onTriggered: () => {
      setMobileNavOpen(false);
      // eslint-disable-next-line array-callback-return
      overlayRef.current.map(nav => {
        animateNavigation(nav, false);
      });
      setSelectedNav(0);
      setNotActive();
    }
  });

  useEffect(() => {
    // Handle navigation when breakpoint change.
    if (selectedNav > 0 && !isMobile) {
      setMobileNavOpen(true);
    } else if (selectedNav > 0 && isMobile) {
      setMobileNavOpen(false);
    }
  }, [isMobile, selectedNav]);

  return (
    <nav aria-label="main" role="navigation" ref={ref}>
      <Box
        as="ul"
        aria-label="Hoved navigation"
        className={classnames(
          className,
          styles.navigation,
          selectedNav > 0 && styles.navigationActive
        )}
        {...props}
      >
        {navigation &&
          navigation.map((menuItem: MappedMenuItemProps, index: number) => {
            const keyIndex = index + 1;
            if (menuItem.UmbracoNaviHide) return null;
            return (
              <Box as="li" key={menuItem.Id}>
                <button
                  onClick={toggle}
                  data-key={keyIndex}
                  className={classnames(
                    styles.buttonNavigation,
                    mobileNavOpen && styles.buttonNavigationMobileNavActive,
                    useDarkHeader && styles.darkTheme,
                    selectedNav > 0 && styles.buttonNavigationNavActive,
                    selectedNav > 0 &&
                      selectedNav !== keyIndex &&
                      styles.buttonNavigationNavInActive
                  )}
                  id={`navid_${keyIndex}`}
                  aria-controls={`controlid_${keyIndex}`}
                  aria-expanded={selectedNav === keyIndex}
                >
                  {menuItem.Name}
                  <NavigationIcon
                    active={selectedNav === keyIndex}
                    className={ classnames(styles.naivgationIcon,
                      useDarkHeader && styles.darkTheme,
                    )}
                  />
                </button>

                <div
                  className={classnames(
                    styles.navigationOverlay,
                    selectedNav === keyIndex && styles.navigationOverlayActive
                  )}
                  id={`controlid_${keyIndex}`}
                  aria-labelledby={`navid_${keyIndex}`}
                  hidden
                  ref={(el: HTMLDivElement): HTMLDivElement =>
                    (overlayRef.current[index] = el)
                  }
                >
                  <button
                    onClick={toggle}
                    className={styles.closeButton}
                  ></button>
                  <Container className={styles.subNavigation}>
                    {menuItem.MappedMenuItems &&
                      menuItem.MappedMenuItems.map(
                        (
                          menuPageItem: MappedMenuPageItemsProps,
                          index: number
                        ) => {
                          const animateEachChild = {
                            ...animeChildrenProps,
                            ...{ delay: (index * 100) / 1.5 }
                          };
                          if (
                            selectedNav === keyIndex &&
                            !menuPageItem.UmbracoNaviHide
                          ) {
                            return (
                              <Box key={menuPageItem.Id}>
                                <Anime {...animateEachChild}>
                                  <Heading as="h3">{menuPageItem.Name}</Heading>
                                  <Box as="ul" key={menuPageItem.Id}>
                                    {menuPageItem.MappedMenuPageItems &&
                                      menuPageItem.MappedMenuPageItems.map(
                                        (menuPageItemItem: SubItemProps) => {
                                          if (menuPageItemItem.UmbracoNaviHide)
                                            return null;
                                          return (
                                            <Box
                                              as="li"
                                              key={menuPageItemItem.Id}
                                            >
                                              <Link
                                                href={menuPageItemItem.Url}
                                                className={styles.linkItem}
                                                {...(menuPageItemItem.Target && {
                                                  target:
                                                    menuPageItemItem.Target
                                                })}
                                              >
                                                {menuPageItemItem.Name}
                                              </Link>
                                            </Box>
                                          );
                                        }
                                      )}
                                  </Box>
                                </Anime>
                              </Box>
                            );
                          } else {
                            return null;
                          }
                        }
                      )}
                  </Container>
                </div>
              </Box>
            );
          })}
      </Box>
      <Button
        className={classnames(styles.buttonMobileburger, useDarkHeader && styles.buttonMobileburgerDark)}
        onClick={toggleMobileNavigation}
        aria-label="Burger navigation"
      >
        <Box
          className={classnames(
            styles.mobileNavigation,
            useDarkHeader && styles.mobileNavigationDark,
            mobileNavOpen && styles.mobileNavigationActive
          )}
        >
          <NavigationIcon
            active={mobileNavOpen}
            activeClassName={styles.mobileNavigationIconActive}
            className={styles.mobileNavigationIcon}
          />
        </Box>
      </Button>
    </nav>
  );
};

Navigation.defaultProps = {};

export { Navigation };
